/**
 * lib/reportFont.ts
 * ------------------------------------------------------------------
 * Registers the embedded Lato weights on a jsPDF document so the student
 * report renders in a modern sans instead of the built-in Helvetica. Call
 * `registerReportFont(pdf)` once per document BEFORE any setFont/text call;
 * it returns the family name to pass to `setFont` — 'Lato' on success, or
 * 'helvetica' if registration ever fails (so the report still renders).
 *
 * The font bytes live in reportFontData.ts (subset, base64). Registration is
 * idempotent per document (guarded by a WeakSet) so the per-student loop in
 * buildClassReportPdf doesn't re-add the VFS files for every student.
 * ------------------------------------------------------------------
 */
import type { jsPDF } from 'jspdf';
import { LATO_REGULAR, LATO_BOLD, LATO_ITALIC } from './reportFontData';

export const REPORT_FONT = 'Lato';
const done = new WeakSet<object>();

export function registerReportFont(pdf: jsPDF): string {
  try {
    if (done.has(pdf)) return REPORT_FONT;
    pdf.addFileToVFS('Lato-Regular.ttf', LATO_REGULAR);
    pdf.addFont('Lato-Regular.ttf', REPORT_FONT, 'normal');
    pdf.addFileToVFS('Lato-Bold.ttf', LATO_BOLD);
    pdf.addFont('Lato-Bold.ttf', REPORT_FONT, 'bold');
    pdf.addFileToVFS('Lato-Italic.ttf', LATO_ITALIC);
    pdf.addFont('Lato-Italic.ttf', REPORT_FONT, 'italic');
    done.add(pdf);
    return REPORT_FONT;
  } catch {
    return 'helvetica';
  }
}
